import React, { useMemo } from 'react';
import type { TableColumnDef } from '../../../shared/table/types.ts';
import { AppDataTable } from '../../../shared/table/AppDataTable.tsx';
import { useAuditorIssueLedger } from '../../../hooks/analytics/useAuditorReports.ts';
import { RefreshMeta, StatusChip } from '../controller/shared.tsx';
import type { AuditorIssueLedgerRow } from '../../../request/analytics/auditorReports.ts';

/**
 * Report #40 — Issue Ledger. Read-only issue table: issue / period /
 * classification / linked controls / action-plan completion (done-total).
 */
export const IssueLedgerTable: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useAuditorIssueLedger();
  const rows = useMemo(() => data ?? [], [data]);

  const columns = useMemo<TableColumnDef<AuditorIssueLedgerRow>[]>(
    () => [
      {
        id: 'title',
        columnLabel: 'Issue',
        header: () => 'Issue',
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="font-medium text-gray-900 truncate">{row.original.title}</p>
            {row.original.is_repeat ? (
              <span className="text-xs text-amber-600">Repeat · vintage {row.original.vintage_count}</span>
            ) : null}
          </div>
        ),
        widthPercent: 22,
      },
      {
        id: 'status',
        columnLabel: 'Status',
        header: () => 'Status',
        cell: ({ row }) => <StatusChip value={row.original.status} />,
        widthPercent: 12,
      },
      {
        id: 'classification',
        columnLabel: 'Classification',
        header: () => 'Classification',
        cell: ({ row }) => row.original.classification ?? '—',
        widthPercent: 14,
      },
      {
        id: 'period',
        columnLabel: 'Period',
        header: () => 'Period',
        cell: ({ row }) => row.original.period ?? '—',
        widthPercent: 10,
      },
      {
        id: 'department',
        columnLabel: 'Department',
        header: () => 'Department',
        cell: ({ row }) => row.original.department ?? '—',
        widthPercent: 12,
      },
      {
        id: 'linked_controls',
        columnLabel: 'Linked Controls',
        header: () => 'Linked Controls',
        cell: ({ row }) => row.original.linked_controls ?? '—',
        widthPercent: 16,
        textOverflowBehavior: 'truncate',
      },
      {
        id: 'actions',
        columnLabel: 'Action Plans',
        header: () => 'Action Plans',
        cell: ({ row }) => (
          <span>
            <span className="text-green-600 font-medium">{row.original.resolved_actions}</span>
            {' / '}
            <span className="text-gray-700 font-medium">{row.original.total_actions}</span>
            <span className="text-gray-400"> done</span>
          </span>
        ),
        widthPercent: 14,
      },
    ],
    []
  );

  return (
    <AppDataTable<AuditorIssueLedgerRow>
      data={rows}
      columns={columns}
      loading={isLoading}
      error={isError ? error?.message ?? 'Failed to load the issue ledger.' : null}
      emptyMessage="No issues recorded."
      searchable
      searchPlaceholder="Search issues..."
      paginated
      defaultPageSize={10}
      showPageSizeSelector
      headerStart={
        <div className="flex w-full justify-end">
          <RefreshMeta updatedAt={dataUpdatedAt} onRefresh={refetch} isFetching={isFetching} />
        </div>
      }
    />
  );
};

export default IssueLedgerTable;
